// Utility to check if user has incomplete onboarding state
// This helps sync onboarding across browser windows/tabs

/**
 * Check if user has incomplete onboarding state
 * Returns true if user is in the middle of onboarding (not complete)
 */
export function hasIncompleteOnboarding(): boolean {
  // Get the persisted state directly from localStorage
  // This works even if Zustand store hasn't initialized yet
  // localStorage is shared across browser tabs/windows for the same domain
  try {
    const stored = localStorage.getItem('rollops-onboarding');
    if (!stored) {
      return false;
    }

    const parsed = JSON.parse(stored);
    const state = parsed.state;

    if (!state) {
      return false;
    }

    // Check if onboarding is marked as complete
    const isComplete = state?.currentSection === 'complete';
    if (isComplete) {
      return false;
    }

    // Check if user has a companyId but onboarding is not complete
    const hasCompanyId = !!state?.companyId;
    const hasActiveSection = state?.currentSection && 
      ['company', 'locations', 'billing', 'subscriptions'].includes(state.currentSection);

    // If they have a company ID but section is not complete, they're mid-onboarding
    if (hasCompanyId && hasActiveSection) {
      return true;
    }

    // Also check if they have company data but no companyId yet (early stages)
    const hasCompanyData = state?.companyData && (
      state.companyData.street ||
      state.companyData.name ||
      state.companyData.email ||
      state.companyData.city
    );

    // If they have company data and are in an active section, they're mid-onboarding
    if (hasCompanyData && hasActiveSection) {
      return true;
    }

    return false;
  } catch (error) {
    console.warn('[onboardingCheck] Error checking onboarding state:', error);
    return false;
  }
}

/**
 * Get the current onboarding section from persisted state
 */
export function getCurrentOnboardingSection(): string | null {
  try {
    const stored = localStorage.getItem('rollops-onboarding');
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored);
    return parsed.state?.currentSection || null;
  } catch (error) {
    console.warn('[onboardingCheck] Error getting onboarding section:', error);
    return null;
  }
}

/**
 * Get the onboarding URL based on current section
 */
export function getOnboardingUrl(): string {
  const section = getCurrentOnboardingSection();
  
  switch (section) {
    case 'company':
      return '/onboarding';
    case 'locations':
      return '/onboarding'; // Locations now inline, not separate route
    case 'billing':
      return '/onboarding/billing';
    case 'subscriptions':
      return '/onboarding';
    case 'complete':
      return '/dashboard';
    default:
      return '/onboarding';
  }
}
